import React from 'react';
import { useLocation } from 'react-router-dom';
import { TextField, Typography } from '@mui/material';

import { Popup } from '@commonComponents';
import ShapeAreaEditor from '@commonComponents/GoogleMap/ShapeAreaEditor';
import type { AreaShape } from '@commonComponents/GoogleMap/ShapeAreaEditor';
import { createTenantRoute, getTenantRoutes } from '@api/tenantRoutes';
import { getTenantProfile } from '@api/tenants';
import { useSelector } from '@hooks';
import { selectUserProfile } from '@actions/users';
import toast from '@utils/toast';
import type { TenantRoutePoint } from '@types';
import { HOME_ROUTE_RADIUS_M, squareRoutePoints } from '@utils/tenantRoutes';

const ROUTE_ID = 1;

const storageKey = (userId: number) => `routes-onboarding-skipped-${userId}`;

function hasSkipped(userId: number) {
    try {
        return window.localStorage.getItem(storageKey(userId)) === 'true';
    } catch (e) {
        return false;
    }
}

function rememberSkipped(userId: number) {
    try {
        window.localStorage.setItem(storageKey(userId), 'true');
    } catch (e) {
        // Storage unavailable: the modal shows again next time
    }
}

/** Offers admins of carriers without routes to create a first route around the company's home. */
const RoutesOnboarding: React.FC = () => {
    const userProfile = useSelector(selectUserProfile());
    const { pathname, search } = useLocation();
    // Dev-only preview: ?routesOnboarding=preview opens the modal even with routes or a stored skip
    const preview = process.env.NODE_ENV === 'development'
        && new URLSearchParams(search).get('routesOnboarding') === 'preview';
    const [open, setOpen] = React.useState(false);
    const [name, setName] = React.useState('Hemområde');
    const [points, setPoints] = React.useState<TenantRoutePoint[]>([]);
    const [shape, setShape] = React.useState<AreaShape>('polygon');
    const [circle, setCircle] = React.useState<{ center: TenantRoutePoint, radiusM: number } | null>(null);
    const [saving, setSaving] = React.useState(false);

    const userId = userProfile?.id;
    const eligible = !!userId && !!userProfile?.isAdmin && !!userProfile?.isCarrier;

    React.useEffect(() => {
        if (!eligible || !userId || (!preview && hasSkipped(userId))) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const [routes, profile] = await Promise.all([getTenantRoutes(), getTenantProfile()]);
                if (cancelled || (!preview && routes.length > 0) || !profile?.lat || !profile?.lng) return;
                setPoints(squareRoutePoints(profile.lat, profile.lng));
                setCircle({ center: { lat: profile.lat, lng: profile.lng }, radiusM: HOME_ROUTE_RADIUS_M });
                setShape('polygon');
                setOpen(true);
            } catch (e) {
                // Onboarding is optional; stay silent if the data can't be fetched.
            }
        })();
        return () => { cancelled = true; };
    }, [eligible, userId, preview]);

    const skip = () => {
        if (userId) rememberSkipped(userId);
        setOpen(false);
    };

    const save = async () => {
        if (!name.trim()) {
            toast('Rutten måste ha ett namn');
            return;
        }
        setSaving(true);
        try {
            await createTenantRoute({
                name: name.trim(),
                ...(shape === 'circle' && circle
                    ? { shape: { type: 'circle' as const, center: circle.center, radius: circle.radiusM } }
                    : { points, shape: null }),
                isPublic: true
            });
            setOpen(false);
            toast('Rutten är sparad. Du hittar och ändrar dina rutter under Administration.');
        } catch (e) {
            toast(e, 'Kunde inte spara rutten, försök igen');
        } finally {
            setSaving(false);
        }
    };

    // The routes page is the place to create routes, so don't cover it.
    if (!open || pathname.startsWith('/routes')) return null;

    return (
        <Popup
            open
            title="Skapa er första rutt"
            fullWidth
            maxWidth="md"
            okLabel="Spara"
            cancelLabel="Hoppa över"
            handleOk={save}
            handleClose={skip}
            isSubmitting={saving}
            okDisabled={!name.trim()}
        >
            <Typography sx={{ mb: 2 }}>
                Rutter används som områden i era bevakningar. Vi har ritat ett område runt er hemort.
                Välj form, dra i ytan eller punkterna för att anpassa det, eller hoppa över och skapa rutter senare.
            </Typography>
            <TextField
                label="Namn"
                size="small"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                inputProps={{ maxLength: 80 }}
                required
                sx={{ mb: 2 }}
            />
            <ShapeAreaEditor
                shape={shape}
                onShapeChange={setShape}
                routes={[{ id: ROUTE_ID, name, points }]}
                selectedId={ROUTE_ID}
                fitToId={ROUTE_ID}
                onPointsChange={(_id: number, next: TenantRoutePoint[]) => setPoints(next)}
                circleCenter={circle?.center ?? null}
                circleRadiusM={circle?.radiusM ?? HOME_ROUTE_RADIUS_M}
                onCircleChange={(center, radiusM) => setCircle({ center, radiusM })}
                height="clamp(250px, 45vh, 420px)"
            />
        </Popup>
    );
};

export default RoutesOnboarding;
